import {computed, ref} from 'vue';
import type {PortfolioItemCreateRequest} from '@api/models/PortfolioItemCreateRequest';
import type {PortfolioItemUpdateRequest} from '@api/models/PortfolioItemUpdateRequest';
import {defineStore} from 'pinia';
import {useTranslation} from '#imports';
import {useErrorHandler} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {
    Configuration,
    PortfolioItemsApi,
    type FetchError,
    type PortfolioInspirationItemResource,
    type PortfolioItemReportRequest,
    type PortfolioItemResource,
    type PortfolioItemSummaryResource,
} from '@api';
import type {EditorJsonNode} from '~/features/portfolio/editor/composable/portfolioEditorItems';
import {extractBase64Images} from '~/features/portfolio/editor/composable/uploadEditorImages';
import type {TranslationKey} from '#build/i18n';

export const usePortfolioItemsStore = defineStore('portfolio-items', () => {
    const apiConfiguration = new Configuration({basePath: ''});
    const portfolioItemsApi = new PortfolioItemsApi(apiConfiguration);
    const {handleFetchError} = useErrorHandler();
    const {t} = useTranslation();

    const portfolio = ref<PortfolioItemSummaryResource[]>([]);
    const hasPublishedItems = computed(() => portfolio.value.some((item) => item.published));

    const UNPROCESSABLE_ENTITY_STATUS_CODE = 422;
    const MAX_IMAGES_SIZE_BYTES = 10 * 1024 * 1024;

    type PortfolioFetchErrorCode =
        | 'PORTFOLIO_FILE_TOO_LARGE'
        | 'PORTFOLIO_THUMBNAIL_TOO_LARGE'
        | 'PORTFOLIO_FILE_TYPE_NOT_ALLOWED'
        | 'PORTFOLIO_INVALID_LINK'
        | 'PORTFOLIO_INVALID_VIDEO_URL'
        | 'PORTFOLIO_PAYLOAD_TOO_LARGE';

    type PortfolioErrorSnackbarConfig = {
        message: TranslationKey;
        header?: TranslationKey;
        level: 'warning' | 'error';
    };

    const portfolioErrorSnackbarConfigs: Record<PortfolioFetchErrorCode, PortfolioErrorSnackbarConfig> = {
        PORTFOLIO_FILE_TOO_LARGE: {
            message: 'profile.section.portfolio.editor.upload.error.file.too.large',
            header: 'profile.section.portfolio.editor.upload.error.file.too.large.header',
            level: 'warning',
        },
        PORTFOLIO_THUMBNAIL_TOO_LARGE: {
            message: 'profile.section.portfolio.editor.upload.error.file.too.large',
            header: 'profile.section.portfolio.editor.upload.error.file.too.large.header',
            level: 'warning',
        },
        PORTFOLIO_FILE_TYPE_NOT_ALLOWED: {
            message: 'profile.section.portfolio.editor.upload.error.file.type.not.allowed',
            header: 'profile.section.portfolio.editor.upload.error.file.type.not.allowed.header',
            level: 'warning',
        },
        PORTFOLIO_INVALID_LINK: {
            header: 'profile.section.portfolio.link.blocked',
            message: 'profile.section.portfolio.link.blocked.message',
            level: 'warning',
        },
        PORTFOLIO_INVALID_VIDEO_URL: {
            message: 'profile.section.portfolio.editor.video.link.blocked',
            level: 'warning',
        },
        PORTFOLIO_PAYLOAD_TOO_LARGE: {
            message: 'profile.section.portfolio.editor.upload.error.payload.too.large',
            header: 'profile.section.portfolio.editor.upload.error.payload.too.large.header',
            level: 'warning',
        },
    };

    function getPortfolioErrorCode(error: unknown): string | undefined {
        return (error as {cause?: {response?: {_data?: {properties?: {code?: string}}}}}).cause?.response?._data?.properties?.code;
    }

    function showPortfolioErrorSnackbar(code: string | undefined): void {
        const snackbarConfig = portfolioErrorSnackbarConfigs[code as PortfolioFetchErrorCode];

        if (!snackbarConfig) {
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
            return;
        }

        pushVJoySnackbar({
            props: {
                message: t(snackbarConfig.message),
                header: snackbarConfig.header ? t(snackbarConfig.header) : undefined,
                level: snackbarConfig.level,
            },
        });
    }

    function checkFilesSize(imageFiles: File[], thumbnailFile?: File): boolean {
        const totalBytes = imageFiles.reduce((sum, f) => sum + f.size, 0) + (thumbnailFile?.size ?? 0);
        if (totalBytes > MAX_IMAGES_SIZE_BYTES) {
            showPortfolioErrorSnackbar('PORTFOLIO_PAYLOAD_TOO_LARGE');
            return false;
        }
        return true;
    }

    function handlePortfolioFetchError(error: unknown): void {
        const fetchError = error as FetchError;

        if (fetchError.statusCode !== UNPROCESSABLE_ENTITY_STATUS_CODE) {
            handleFetchError(fetchError, {customErrorMessage: t('generic.error.unexpected')});
            return;
        }

        showPortfolioErrorSnackbar(getPortfolioErrorCode(error));
    }

    function upsertPortfolioItem(updatedItem: PortfolioItemResource): void {
        const existingItemIndex = portfolio.value.findIndex((item) => item.id === updatedItem.id);

        if (existingItemIndex === -1) {
            portfolio.value = [...portfolio.value, updatedItem];
            return;
        }

        portfolio.value = portfolio.value.map((item) => (item.id === updatedItem.id ? updatedItem : item));
    }

    async function getPortfolioItemById(id: string): Promise<PortfolioItemResource> {
        try {
            const item = await portfolioItemsApi.getPortfolioItemById({id});
            upsertPortfolioItem(item);
            return item;
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            throw e;
        }
    }

    async function listPortfolioItems(profileId: string, publishedOnly: boolean = false): Promise<PortfolioItemSummaryResource[]> {
        try {
            const items = await portfolioItemsApi.listPortfolioItems({profileId, publishedOnly});
            portfolio.value = items;
            return items;
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            throw e;
        }
    }

    async function createPortfolioItem(createPortfolioItemRequest: PortfolioItemCreateRequest, thumbnailFile?: File): Promise<PortfolioItemResource> {
        const {cleanedContentBlocks, imageFiles, base64SrcToIndex} = extractBase64Images(createPortfolioItemRequest.contentBlocks as EditorJsonNode);
        const {resolvedThumbnail, resolvedThumbnailFile} = resolveThumbnailForRequest(
            createPortfolioItemRequest.thumbnail ?? '',
            base64SrcToIndex,
            thumbnailFile,
        );
        if (!checkFilesSize(imageFiles, resolvedThumbnailFile)) {
            throw new Error('PAYLOAD_TOO_LARGE');
        }
        try {
            const createdItem = await portfolioItemsApi.createPortfolioItem({
                data: {...createPortfolioItemRequest, contentBlocks: cleanedContentBlocks, thumbnail: resolvedThumbnail},
                images: imageFiles,
                thumbnail: resolvedThumbnailFile,
            });
            upsertPortfolioItem(createdItem);
            return createdItem;
        } catch (e) {
            handlePortfolioFetchError(e);
            throw e;
        }
    }

    async function updatePortfolioItemById(
        id: string,
        updatePortfolioItemRequest: PortfolioItemUpdateRequest,
        thumbnailFile?: File,
    ): Promise<PortfolioItemResource> {
        const {cleanedContentBlocks, imageFiles, base64SrcToIndex} = extractBase64Images(updatePortfolioItemRequest.contentBlocks as EditorJsonNode);
        const {resolvedThumbnail, resolvedThumbnailFile} = resolveThumbnailForRequest(
            updatePortfolioItemRequest.thumbnail ?? '',
            base64SrcToIndex,
            thumbnailFile,
        );
        if (!checkFilesSize(imageFiles, resolvedThumbnailFile)) {
            throw new Error('PAYLOAD_TOO_LARGE');
        }
        try {
            const updatedItem = await portfolioItemsApi.updatePortfolioItemById({
                id,
                data: {...updatePortfolioItemRequest, contentBlocks: cleanedContentBlocks, thumbnail: resolvedThumbnail},
                images: imageFiles,
                thumbnail: resolvedThumbnailFile,
            });
            upsertPortfolioItem(updatedItem);
            return updatedItem;
        } catch (e) {
            handlePortfolioFetchError(e);
            throw e;
        }
    }

    function resolveThumbnailForRequest(
        thumbnail: string,
        base64SrcToIndex: Map<string, number>,
        thumbnailFile?: File,
    ): {resolvedThumbnail: string; resolvedThumbnailFile: File | undefined} {
        if (thumbnailFile) {
            return {resolvedThumbnail: '', resolvedThumbnailFile: thumbnailFile};
        }
        if (thumbnail.startsWith('data:')) {
            const index = base64SrcToIndex.get(thumbnail);
            if (index !== undefined) {
                return {resolvedThumbnail: `@img:${index}`, resolvedThumbnailFile: undefined};
            }
        }
        return {resolvedThumbnail: thumbnail, resolvedThumbnailFile: undefined};
    }

    async function deletePortfolioItemById(id: string): Promise<void> {
        try {
            await portfolioItemsApi.deletePortfolioItemById({id});
            portfolio.value = portfolio.value.filter((item) => item.id !== id);
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
        }
    }

    async function reportPortfolioItemById(id: string, reportRequest: PortfolioItemReportRequest): Promise<void> {
        await portfolioItemsApi.reportPortfolioItemById({id, portfolioItemReportRequest: reportRequest});
    }

    async function reorderPortfolioItems(profileId: string, orderedIds: string[]): Promise<void> {
        try {
            await portfolioItemsApi.reorderPortfolioItems({requestBody: orderedIds});
            await listPortfolioItems(profileId);
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
        }
    }

    async function listInspirationPortfolioItems(): Promise<PortfolioInspirationItemResource[]> {
        try {
            return await portfolioItemsApi.listInspirationPortfolioItems();
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            throw e;
        }
    }

    return {
        portfolio,
        hasPublishedItems,
        getPortfolioItemById,
        listPortfolioItems,
        listInspirationPortfolioItems,
        createPortfolioItem,
        updatePortfolioItemById,
        deletePortfolioItemById,
        reorderPortfolioItems,
        reportPortfolioItemById,
    };
});
